import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";
import {
  Search,
  Filter,
  MoreHorizontal,
  UserCheck,
  UserX,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Loader2,
  Calendar,
  Mail,
} from "lucide-react";
import {
  UserManagementService,
  UserProfile,
  UserManagementFilters,
  PaginationParams,
} from "../../services/userManagementService";
import { useToast } from "../../hooks/use-toast";
import { ADMIN_CONFIG } from "../../utils/constants";

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    initial: 0,
    approved: 0,
    disapproved: 0,
    admin: 0,
  });

  // Filter and pagination states
  const [filters, setFilters] = useState<UserManagementFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
    loadStats();
  }, [filters, pagination]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await UserManagementService.getUsers(filters, pagination);

      if (result.error) {
        setError(result.error);
      } else {
        setUsers(result.data);
        setTotalCount(result.count);
      }
    } catch (error) {
      setError("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await UserManagementService.getUserStats();
      if (result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Failed to load user stats:", error);
    }
  };

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: keyof UserManagementFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedUsers([]); // Clear selections when filtering
  };

  const clearAllFilters = () => {
    setFilters({});
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedUsers([]);
  };

  const handleApproveUser = async (userId: string) => {
    try {
      const result = await UserManagementService.approveUser(userId);

      if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      } else {
        loadUsers();
        loadStats();
        toast({
          title: "Success",
          description: "User approved successfully",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve user",
        variant: "destructive",
      });
    }
  };

  const handleDisapproveUser = async (userId: string) => {
    try {
      const result = await UserManagementService.disapproveUser(userId);

      if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      } else {
        loadUsers();
        loadStats();
        toast({
          title: "Success",
          description: "User disapproved successfully",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disapprove user",
        variant: "destructive",
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const result = await UserManagementService.bulkApproveUsers(
        selectedUsers
      );

      if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      } else {
        setSelectedUsers([]);
        loadUsers();
        loadStats();
        toast({
          title: "Success",
          description: `${selectedUsers.length} users approved successfully`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve users",
        variant: "destructive",
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4 text-purple-500" />;
      case "user":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "disapproved":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "initial":
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return (
          <Badge variant="default" className="bg-purple-100 text-purple-800">
            Admin
          </Badge>
        );
      case "user":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Approved
          </Badge>
        );
      case "disapproved":
        return <Badge variant="destructive">Disapproved</Badge>;
      case "initial":
        return (
          <Badge variant="secondary" className="bg-orange-100 text-orange-800">
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = Math.ceil(
    totalCount / (pagination.limit || ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE)
  );

  return (
    <div className="space-y-6">
      {/* Statistics Cards - Clickable for quick filtering */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            !filters.role ? "ring-2 ring-primary" : ""
          }`}
          onClick={() => {
            setFilters({});
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.role === "initial" ? "ring-2 ring-orange-500" : ""
          }`}
          onClick={() => handleFilterChange("role", "initial")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats.initial}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.role === "user" ? "ring-2 ring-green-500" : ""
          }`}
          onClick={() => handleFilterChange("role", "user")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.approved}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.role === "disapproved" ? "ring-2 ring-red-500" : ""
          }`}
          onClick={() => handleFilterChange("role", "disapproved")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Disapproved</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.disapproved}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.role === "admin" ? "ring-2 ring-purple-500" : ""
          }`}
          onClick={() => handleFilterChange("role", "admin")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.admin}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          {/* Active Filters Indicator */}
          {(filters.role || filters.search) && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    Active Filters:
                  </span>
                  <div className="flex gap-2">
                    {filters.role && (
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800"
                      >
                        Role:{" "}
                        {filters.role === "initial"
                          ? "Pending"
                          : filters.role === "user"
                          ? "Approved"
                          : filters.role === "disapproved"
                          ? "Disapproved"
                          : filters.role === "admin"
                          ? "Admin"
                          : filters.role}
                      </Badge>
                    )}
                    {filters.search && (
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800"
                      >
                        Search: "{filters.search}"
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Clear All
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Filter className="mr-2 h-4 w-4" />
                    {filters.role
                      ? filters.role === "initial"
                        ? "Pending"
                        : filters.role === "user"
                        ? "Approved"
                        : filters.role === "disapproved"
                        ? "Disapproved"
                        : filters.role === "admin"
                        ? "Admin"
                        : "Filter"
                      : "All Users"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setFilters({})}>
                    <Users className="mr-2 h-4 w-4" />
                    All Users ({stats.total})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("role", "initial")}
                  >
                    <Clock className="mr-2 h-4 w-4 text-orange-500" />
                    Pending ({stats.initial})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("role", "user")}
                  >
                    <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                    Approved ({stats.approved})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("role", "disapproved")}
                  >
                    <XCircle className="mr-2 h-4 w-4 text-red-500" />
                    Disapproved ({stats.disapproved})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("role", "admin")}
                  >
                    <Shield className="mr-2 h-4 w-4 text-purple-500" />
                    Admin ({stats.admin})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {pagination.limit ||
                      ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE}{" "}
                    per page
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() =>
                      setPagination((prev) => ({ ...prev, limit: 10, page: 1 }))
                    }
                  >
                    10 per page
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setPagination((prev) => ({ ...prev, limit: 25, page: 1 }))
                    }
                  >
                    25 per page
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setPagination((prev) => ({ ...prev, limit: 50, page: 1 }))
                    }
                  >
                    50 per page
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        limit: 100,
                        page: 1,
                      }))
                    }
                  >
                    100 per page
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {selectedUsers.length > 0 && (
                <Button
                  onClick={handleBulkApprove}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  Approve Selected ({selectedUsers.length})
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Users ({totalCount.toLocaleString()})
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {filters.role && (
              <Badge variant="outline" className="ml-2">
                {filters.role === "initial"
                  ? "Pending"
                  : filters.role === "user"
                  ? "Approved"
                  : filters.role === "disapproved"
                  ? "Disapproved"
                  : filters.role === "admin"
                  ? "Admin"
                  : filters.role}{" "}
                Only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading users...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No users found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedUsers.length === users.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers(users.map((u) => u.id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(
                                selectedUsers.filter((id) => id !== user.id)
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt={user.name || user.email || "User"}
                              className="h-8 w-8 rounded-full"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-sm font-medium">
                                {(user.name || user.email || "U")
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {user.name || user.full_name || "Unknown"}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getRoleIcon(user.role)}
                          {getRoleBadge(user.role)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDate(user.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {(user.role === "initial" ||
                              user.role === "disapproved") && (
                              <DropdownMenuItem
                                onClick={() => handleApproveUser(user.id)}
                                className="text-green-600"
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                Approve User
                              </DropdownMenuItem>
                            )}
                            {user.role === "user" && (
                              <DropdownMenuItem
                                onClick={() => handleDisapproveUser(user.id)}
                                className="text-red-600"
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Disapprove User
                              </DropdownMenuItem>
                            )}
                            {user.role === "initial" && (
                              <DropdownMenuItem
                                onClick={() => handleDisapproveUser(user.id)}
                                className="text-red-600"
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Reject User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Enhanced Pagination for Large Datasets */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing{" "}
                    {((pagination.page || 1) - 1) *
                      (pagination.limit ||
                        ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE) +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      (pagination.page || 1) *
                        (pagination.limit ||
                          ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE),
                      totalCount
                    )}{" "}
                    of {totalCount.toLocaleString()} results
                    {filters.role && (
                      <span className="ml-2 text-primary">
                        (filtered by{" "}
                        {filters.role === "initial" ? "pending" : filters.role})
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* First Page */}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) <= 1}
                      onClick={() =>
                        setPagination((prev) => ({ ...prev, page: 1 }))
                      }
                    >
                      First
                    </Button>

                    {/* Previous */}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) <= 1}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: (prev.page || 1) - 1,
                        }))
                      }
                    >
                      Previous
                    </Button>

                    {/* Page Numbers (show current and nearby pages) */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        const currentPage = pagination.page || 1;
                        const pages = [];
                        const showPages = 5; // Show 5 page numbers
                        let startPage = Math.max(
                          1,
                          currentPage - Math.floor(showPages / 2)
                        );
                        let endPage = Math.min(
                          totalPages,
                          startPage + showPages - 1
                        );

                        // Adjust start if we're near the end
                        if (endPage - startPage + 1 < showPages) {
                          startPage = Math.max(1, endPage - showPages + 1);
                        }

                        // Add ellipsis at start if needed
                        if (startPage > 1) {
                          pages.push(
                            <Button
                              key="start-ellipsis"
                              variant="ghost"
                              size="sm"
                              disabled
                              className="text-muted-foreground"
                            >
                              ...
                            </Button>
                          );
                        }

                        // Add page numbers
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <Button
                              key={i}
                              variant={
                                i === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setPagination((prev) => ({ ...prev, page: i }))
                              }
                              className="min-w-[40px]"
                            >
                              {i}
                            </Button>
                          );
                        }

                        // Add ellipsis at end if needed
                        if (endPage < totalPages) {
                          pages.push(
                            <Button
                              key="end-ellipsis"
                              variant="ghost"
                              size="sm"
                              disabled
                              className="text-muted-foreground"
                            >
                              ...
                            </Button>
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Next */}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) >= totalPages}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: (prev.page || 1) + 1,
                        }))
                      }
                    >
                      Next
                    </Button>

                    {/* Last Page */}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) >= totalPages}
                      onClick={() =>
                        setPagination((prev) => ({ ...prev, page: totalPages }))
                      }
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
 